import React from "react";
import { Info } from "lucide-react";

import { Card } from "@/components/ui/Card";
import DataTable from "@/components/shared/DataTable";
import UsageSectionHeader from "./UsageSectionHeader";
import { fmt } from "./format";

const FEATURE_COLUMNS = [
  {
    key: "feature",
    label: "Feature",
    // The label is served, not derived: usually the owning module's name, but
    // a module that emits more than one feature names each of them, so
    // candidate matching reads as "(intake)" and "(re-analysis)" on two rows
    // rather than the same name twice. The raw feature string stays in the
    // tooltip — an engineer reading this table needs something to grep for.
    render: (_value, row) => (
      <span title={row.feature}>
        {row.label || row.feature}
        {row.isGuardian && <span className="ml-2 rounded-full bg-tone-slate-soft px-2 py-0.5 text-[10px] text-tone-slate">prompt safety</span>}
      </span>
    ),
  },
  { key: "calls", label: "Calls", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
  { key: "cachedCalls", label: "Cached", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
  { key: "totalTokens", label: "Tokens", align: "right", className: "tabular-nums", render: (value) => fmt(value) },
];

/**
 * The per-feature breakdown. Engineering detail — absent unless asked for, and
 * the third card of the usage report (see `UsageSummary.jsx`).
 *
 * `DataTable` in its local-data mode: `apiData` carries the rows, and no `api`
 * prop means it never fetches. Sorting and pagination are both off — the feature
 * list is short, arrives already ordered by weight, and a reader wants all of it
 * at once, so `pageSize` is the row count.
 */
const FeatureTable = ({ features = [] }) => {
  if (features.length === 0) return null;
  return (
    <Card className="p-5">
      <UsageSectionHeader title="By feature" subtitle="Where the model calls went. The raw feature string is in each row's tooltip." />
      <DataTable columns={FEATURE_COLUMNS} rowKey="feature" apiData={features} sortable={false} showPagination={false} pageSize={features.length} />
      <p className="mt-3 flex items-start gap-2 text-[11px] text-text-secondary">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Prompt-safety rows are the screening pass that runs before a feature's own calls. They are listed separately so they never inflate that feature's count.
        </span>
      </p>
    </Card>
  );
};

export default FeatureTable;
